'use client';
import type { UserMenuProps } from './data';

import React from 'react';
import { ArrowDown, ArrowUp } from 'react-feather';
import { usePathname } from 'next/navigation';

import EditAvatarModal from '../EditAvatarModal';
import LogoutButton from '../LogoutButton';

import ReferralLinkModal from './ReferralLinkModal';

import {
  Box,
  Circle,
  Flex,
  HStack,
  Image,
  Link,
  PromotionCodeActivationModal,
  Stack,
  Text,
  VStack,
} from '@/components';
import { useDisclosure, usePopups, useProfile, useTranslation } from '@/hooks';
import Avatar from '@/images/dashboard/profile.png';
import { routes } from '@/statics';
import AddressesIcon from '@/svgs/dashboard/drawer/addresses.svg';
import AnalyticsIcon from '@/svgs/dashboard/drawer/analytics.svg';
import ArrowUDownLeft from '@/svgs/dashboard/drawer/ArrowUDownLeft.svg';
import CommissionIcon from '@/svgs/dashboard/drawer/commission.svg';
import ContactIcon from '@/svgs/dashboard/drawer/contacts.svg';
import DashboardIcon from '@/svgs/dashboard/drawer/dashboard.svg';
import FavoritesIcon from '@/svgs/dashboard/drawer/favorites.svg';
import LearningPortalIcon from '@/svgs/dashboard/drawer/learningPortal.svg';
import NotificationIcon from '@/svgs/dashboard/drawer/notification.svg';
import OfferingProductIcon from '@/svgs/dashboard/drawer/offeringProduct.svg';
import PersonalInfoIcon from '@/svgs/dashboard/drawer/personalInfo.svg';
import MyLinkIcon from '@/svgs/dashboard/drawer/personalLink.svg';
import PromotionCode from '@/svgs/dashboard/drawer/promotionCode.svg';
import StepsToSuccessIcon from '@/svgs/dashboard/drawer/promotionNextMonth.svg';
import PurchaseRecordsIcon from '@/svgs/dashboard/drawer/purchaseRecordsIcon.svg';
import SendingTicketIcon from '@/svgs/dashboard/drawer/sendingTicket.svg';
import UserTeam from '@/svgs/dashboard/drawer/UsersThree.svg';

const UserMenu: UserMenuProps = ({ isDashboardPage, onClose }) => {
  const { t } = useTranslation('components.dashboard.drawer');
  const [showMore, setShowMore] = React.useState<boolean>(false);
  const { data: profile } = useProfile();
  const pathname = usePathname();
  const { data: popups } = usePopups({
    profile: Boolean(profile),
    isRead: false,
  });
  const {
    isOpen: isOpenPromoCode,
    onOpen: onOpenPromoCode,
    onClose: onClosePromoCode,
  } = useDisclosure();
  const {
    isOpen: isOpenReferralLink,
    onOpen: onOpenReferralLink,
    onClose: onCloseReferralLink,
  } = useDisclosure();
  const isAgent = profile?.user_type === 'agent';
  const isSuspended = profile?.is_suspended;
  const hasRollback = profile?.has_rollback;

  const data = [
    {
      title: routes.dashboard.title,
      icon: <DashboardIcon />,
      link: routes.dashboard.url,
      type: 'internal',
      isVisible: true,
    },
    {
      title: t('promotionCode'),
      icon: <PromotionCode />,
      link: '',
      onClick: () => {
        onOpenPromoCode();
      },
      type: 'internal',
      isVisible: false,
    },
    {
      title: routes.nextMonthRetailDiscount.title,
      icon: <StepsToSuccessIcon />,
      link: routes.nextMonthRetailDiscount.url,
      type: 'internal',
      isVisible: isAgent && !isSuspended,
    },
    {
      title: routes.rollbacks.title,
      icon: <ArrowUDownLeft />,
      link: routes.rollbacks.url,
      type: 'internal',
      isVisible: hasRollback,
    },
    {
      title: t('myLink'),
      icon: <MyLinkIcon />,
      onClick: onOpenReferralLink,
      type: 'button',
      isVisible: isAgent,
    },
    {
      title: routes.tickets.title,
      icon: <SendingTicketIcon />,
      link: routes.tickets.url,
      type: 'internal',
      isVisible: true,
    },
    {
      title: routes.personalInformation.title,
      icon: <PersonalInfoIcon />,
      link: routes.personalInformation.url,
      type: 'internal',
      isVisible: true,
    },
    {
      title: routes.manageLprs.title,
      icon: <OfferingProductIcon />,
      link: routes.manageLprs.url,
      type: 'internal',
      isVisible: isAgent,
    },
    {
      title: t('learningPortal'),
      icon: <LearningPortalIcon />,
      link: process.env.NEXT_PUBLIC_LIATEAM_METS,
      type: 'external',
      isVisible: isAgent,
    },
    {
      title: routes.teamManagement.title,
      icon: <UserTeam />,
      link: routes.teamManagement.url,
      type: 'internal',
      isVisible: isAgent && !isSuspended,
    },
    {
      title: routes.commission.title,
      icon: <CommissionIcon />,
      link: routes.commission.url,
      type: 'internal',
      isVisible: isAgent && !isSuspended,
    },
    {
      title: routes.orders.title,
      icon: <PurchaseRecordsIcon />,
      link: routes.orders.url,
      type: 'internal',
      isVisible: true,
    },
    {
      title: t('contacts'),
      icon: <ContactIcon />,
      link: process.env.NEXT_PUBLIC_CONTACTS_URL,
      type: 'external',
      isVisible: false,
    },
    {
      title: t('analytics'),
      icon: <AnalyticsIcon />,
      link: process.env.NEXT_PUBLIC_ANALYTICS_URL,
      type: 'external',
      isVisible: false,
    },
    {
      title: t('notifications'),
      icon: (
        <Box position="relative">
          {popups?.list && popups.list.length > 0 && (
            <Circle
              position="absolute"
              bg={'secondary.500'}
              size="8px"
            ></Circle>
          )}
          <NotificationIcon />
        </Box>
      ),
      link: routes.notifications.url,
      type: 'internal',
      isVisible: true,
    },
    {
      title: routes.favorites.title,
      icon: <FavoritesIcon />,
      link: routes.favorites.url,
      type: 'internal',
      isVisible: true,
    },
    {
      title: routes.addresses.title,
      icon: <AddressesIcon />,
      link: routes.addresses.url,
      type: 'internal',
      isVisible: true,
    },
    {
      title: '',
      icon: (
        <LogoutButton
          fontSize="lg !important"
          fontWeight={400}
          onConfirm={onClose}
        />
      ),
      type: 'button',
      isVisible: true,
    },
  ];

  const toggleShowMore = (): void => {
    setShowMore(!showMore);
  };
  // eslint-disable-next-line consistent-return
  const compareLink = (path: string, link: string) => {
    if (path === link) {
      return true;
    }

    const dashboardIndex = link.indexOf('/dashboard/');

    if (dashboardIndex !== -1) {
      const afterDashboard = link.substring(
        dashboardIndex + '/dashboard/'.length
      );

      if (path.includes(`/${afterDashboard}`)) {
        return true;
      }
      return false;
    }
  };
  const LinkBox = ({
    item,
  }: {
    item: {
      title: string;
      icon: React.JSX.Element;
      link?: string;
      type: string;
      onClick?: () => void;
    };
  }) => {
    if (item.link) {
      return (
        <Link
          href={item.link}
          width="100%"
          paddingBlock="10px"
          mb="8px"
          target={item.type === 'external' ? '_blank' : '_self'}
          prefetch={item.type === 'internal'}
          position="relative"
        >
          <Flex gap="38px" ps={3} alignItems="center">
            {item.type === 'internal' &&
              compareLink(pathname, `${item.link}`) && (
                <Box
                  position="absolute"
                  right={0}
                  height="40px"
                  width="6px"
                  bg="secondary.500"
                  borderTopLeftRadius="full"
                  borderBottomLeftRadius="full"
                />
              )}
            <Box
              sx={{
                strokeWidth: '2',
                stroke: 'primary.500',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '29px',
              }}
            >
              {item.icon}
            </Box>
            <Text fontSize="lg" color="primary.500">
              {item.title}
            </Text>
          </Flex>
        </Link>
      );
    }
    return (
      <Flex
        gap="38px"
        ps={3}
        alignItems="center"
        cursor="pointer"
        width="100%"
        paddingBlock="10px"
        mb="8px"
        onClick={() => item.onClick?.()}
        justifyContent="flex-start"
      >
        <Flex
          justifyContent="center"
          alignItems="center"
          sx={{
            strokeWidth: '2',
            stroke: pathname === `${item.link}` ? 'primary.500' : 'text',
          }}
          width={item.title ? '25px' : '100%'}
        >
          {item.icon}
        </Flex>
        {item.title && (
          <Text
            fontSize="lg"
            color={pathname === `${item.link}` ? 'primary.500' : 'text'}
          >
            {item.title}
          </Text>
        )}
      </Flex>
    );
  };
  return (
    <Stack pos="relative">
      {isOpenPromoCode && (
        <PromotionCodeActivationModal
          isOpen={isOpenPromoCode}
          onClose={onClosePromoCode}
        />
      )}
      <ReferralLinkModal
        isOpen={isOpenReferralLink}
        onClose={onCloseReferralLink}
      />
      <HStack alignItems="flex-start" mt="30px" pr={3} gap="20px">
        <Stack pos="relative">
          <Stack
            borderRadius={50}
            w={14}
            h={14}
            align="center"
            justify="center"
            overflow="hidden"
          >
            <Image
              src={profile?.avatar || Avatar}
              alt="avatar"
              width={61}
              height={61}
            />
          </Stack>
          <EditAvatarModal avatar={profile?.avatar as string} />
        </Stack>
        <Box>
          <Text fontSize="lg" fontWeight={700}>
            {profile?.first_name} {profile?.last_name}
          </Text>
          <VStack alignItems="start" gap={0}>
            <Text color="primary.400" fontSize="sm" fontWeight={400}>
              {t('userCode')} {profile?.public_id}
            </Text>
            {profile?.parent_name && (
              <Text color="primary.400" fontSize="sm" fontWeight={400}>
                {t('parentName')} {profile.parent_name}
              </Text>
            )}
          </VStack>
        </Box>
      </HStack>
      <HStack
        display={{ base: 'none', md: 'flex' }}
        mt="32px"
        mb="34px"
        flexDir="column"
        justifyContent="center"
        alignItems="start"
      >
        {data
          .filter((item) => Boolean(item.isVisible))
          .map((item, index) => (
            <Flex key={index} w="100%" alignItems="center">
              <LinkBox key={index} item={item} />
            </Flex>
          ))}
      </HStack>
      {!isDashboardPage && (
        <>
          <HStack
            display={{ base: 'flex', md: 'none' }}
            mt={8}
            mb="26px"
            flexDir="column"
            justifyContent="flex-start"
            alignItems="start"
          >
            {data
              .filter((item) => Boolean(item.isVisible))
              .map((item, index) => (
                <Flex
                  key={index}
                  w="100%"
                  alignItems="center"
                  onClick={item.type === 'button' ? undefined : onClose}
                >
                  <LinkBox key={index} item={item} />
                </Flex>
              ))}
          </HStack>
        </>
      )}
      {isDashboardPage && (
        <>
          {isOpenPromoCode && (
            <PromotionCodeActivationModal
              isOpen={isOpenPromoCode}
              onClose={onClosePromoCode}
            />
          )}
          <HStack
            display={{ base: 'flex', md: 'none' }}
            mt="54px"
            mb="34px"
            gap={2.5}
            flexDir="column"
            justifyContent="center"
            alignItems="start"
          >
            {data
              .filter((item) => Boolean(item.isVisible))
              .slice(0, showMore ? data.length : Math.ceil(data.length / 2))
              .map((item, index) => (
                <Flex key={index} w="100%" alignItems="center">
                  <LinkBox key={index} item={item} />
                </Flex>
              ))}
          </HStack>
          <Box
            gap={3}
            justifyContent="center"
            alignItems="center"
            my={5}
            onClick={toggleShowMore}
            display={{ base: 'flex', md: 'none' }}
            cursor="pointer"
          >
            <Text fontSize="sm" fontWeight={400}>
              {showMore ? t('showLess') : t('showMore')}
            </Text>
            {showMore ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
          </Box>
        </>
      )}
    </Stack>
  );
};

export default UserMenu;
